/**
 * Developers - the public documentation for the Farmgate read API.
 *
 * Public on purpose. Someone deciding whether to pay for Premium needs to see
 * the endpoints, the limits and the actual response shapes first; documentation
 * behind a paywall asks them to buy an unopened box.
 *
 * Every example on this page was captured from the running server rather than
 * written by hand, including the error bodies. Documentation that drifts from
 * the API is worse than none, because it is believed.
 *
 * The cadence note appears more than once by design. The prices are weekly
 * survey observations, not a live feed, and a developer who assumes otherwise
 * will build something that silently reports stale numbers as current.
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Button, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import PublicHero from '../../components/public/PublicHero';
import SectionHeading from '../../components/public/SectionHeading';
import { useThemeMode, MONO } from '../../context/ThemeContext';

const BASE_URL = 'https://farmgate.ng/api/v1';

const SCOPES = [
  ['read:prices', 'Local market price observations', '/prices'],
  ['read:commodities', 'The commodity catalogue', '/commodities'],
  ['read:markets', 'The market and state directory', '/markets'],
  ['read:fx', 'Naira exchange rates, market and official', '/fx'],
];

const ERRORS = [
  ['400', 'The request asked for something that does not exist, like an unknown currency pair. The message says what to use instead.'],
  ['401', 'No key, an unknown key, or a revoked one. The three are answered identically on purpose.'],
  ['403', 'The key is valid but does not carry this scope. You can change what a key may read from the API keys page.'],
  ['429', 'The per-minute rate or the monthly quota is spent. Retry-After says how long to wait.'],
];

/** A code block. Horizontally scrollable, because wrapping JSON is unreadable. */
function Code({ children, palette, onCopy, label }) {
  return (
    <Box sx={{ position: 'relative', mb: 2.5 }}>
      {label && (
        <Typography
          sx={{
            fontFamily: MONO, fontSize: '0.68rem', fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: palette.textMuted, mb: 0.75,
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        component="pre"
        sx={{
          m: 0, p: 2, borderRadius: '10px',
          bgcolor: palette.navy, color: '#E7EDF4',
          fontFamily: MONO, fontSize: '0.78rem', lineHeight: 1.7,
          overflowX: 'auto',
        }}
      >
        <code>{children}</code>
      </Box>
      {onCopy && (
        <Button
          size="small"
          onClick={() => onCopy(children)}
          startIcon={<Icon icon="ph:copy" width={14} height={14} />}
          sx={{
            position: 'absolute', top: label ? 26 : 6, right: 6,
            minWidth: 0, textTransform: 'none', fontSize: '0.72rem',
            color: 'rgba(255,255,255,0.55)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          Copy
        </Button>
      )}
    </Box>
  );
}

/** One endpoint: method, path, what it does, its parameters, a real response. */
function Endpoint({ palette, method = 'GET', path, summary, params, response, note, onCopy }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: '16px',
        bgcolor: palette.card,
        p: { xs: 2.5, md: 3 },
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1 }}>
        <Chip
          label={method}
          size="small"
          sx={{
            height: 21, fontFamily: MONO, fontSize: '0.65rem', fontWeight: 700,
            letterSpacing: '0.06em', bgcolor: palette.accentBg, color: palette.accent,
            borderRadius: '6px',
          }}
        />
        <Typography sx={{ fontFamily: MONO, fontSize: '0.92rem', fontWeight: 700, color: palette.text }}>
          {path}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: '0.9rem', color: palette.textSecondary, lineHeight: 1.65, mb: params ? 2 : 2.5, maxWidth: 720 }}>
        {summary}
      </Typography>

      {params && (
        <TableContainer sx={{ mb: 2.5, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Parameter', 'Meaning'].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: palette.textMuted,
                      borderBottom: `1px solid ${palette.divider}`, py: 1,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {params.map(([name, meaning]) => (
                <TableRow key={name}>
                  <TableCell sx={{ fontFamily: MONO, fontSize: '0.78rem', color: palette.text, borderBottom: `1px solid ${palette.divider}`, py: 1, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {name}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.82rem', color: palette.textSecondary, borderBottom: `1px solid ${palette.divider}`, py: 1, lineHeight: 1.55 }}>
                    {meaning}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Code palette={palette} label="Response" onCopy={onCopy}>{response}</Code>

      {note && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Icon icon="ph:info" width={15} height={15} color={palette.textMuted} style={{ marginTop: 2, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.79rem', color: palette.textSecondary, lineHeight: 1.6 }}>
            {note}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function Developers() {
  const { palette } = useThemeMode();
  const [toast, setToast] = useState(null);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ severity: 'success', msg: 'Copied' });
    } catch {
      setToast({ severity: 'warning', msg: 'Could not reach the clipboard. Select the text and copy it by hand.' });
    }
  };

  const sectionSx = { bgcolor: palette.bg, pt: { xs: 5, md: 7 }, pb: { xs: 1, md: 2 } };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.bg }}>
      <PublicHero
        eyebrow="Developers"
        title="The Farmgate API"
        subtitle="Read Nigerian commodity prices, market coverage and naira exchange rates straight into your own systems. Included with Premium."
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/app/api-keys"
              variant="contained"
              disableElevation
              sx={{
                bgcolor: palette.accent, color: '#fff', fontWeight: 600,
                textTransform: 'none', borderRadius: '9px', px: 2.5,
                '&:hover': { bgcolor: '#1C9550' },
              }}
            >
              Get a key
            </Button>
            <Button
              component={RouterLink}
              to="/app/subscription"
              variant="outlined"
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '9px', px: 2.5,
                borderColor: palette.cardBorder, color: palette.text,
              }}
            >
              See plans
            </Button>
          </Box>
        }
      />

      {/* ============================ 01 What it is ============================ */}
      <Box component="section" sx={sectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            number="01"
            eyebrow="Overview"
            title="What the API gives you"
            subtitle="One base URL, five endpoints, JSON out. No SDK to install."
          />

          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={7}>
              <Typography sx={{ fontSize: '0.95rem', color: palette.textSecondary, lineHeight: 1.75, mb: 2.5 }}>
                Everything on the Farmgate site is readable through the API: the price
                observations we collect from markets across northern Nigeria, the commodity
                and market catalogues that give those observations their meaning, and the
                naira exchange rates we track alongside them.
              </Typography>

              <Box
                sx={{
                  p: 2, borderRadius: '10px', mb: 2.5,
                  bgcolor: palette.warningBg,
                  border: `1px solid ${palette.warning}33`,
                  display: 'flex', gap: 1.25, alignItems: 'flex-start',
                }}
              >
                <Icon icon="ph:clock-countdown" width={17} height={17} color={palette.warning} style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.85rem', color: palette.text, lineHeight: 1.65 }}>
                  <Box component="strong" sx={{ fontWeight: 700 }}>Prices are weekly, not live.</Box>{' '}
                  They come from market surveys uploaded on a weekly cycle. Every price row
                  carries the date it was observed, and you should show that date to your
                  own users rather than the time you fetched it. Exchange rates update
                  daily, on CBN's publication schedule rather than ours.
                </Typography>
              </Box>

              <Code palette={palette} label="Base URL" onCopy={copy}>{BASE_URL}</Code>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  border: `1px solid ${palette.cardBorder}`, borderRadius: '16px',
                  bgcolor: palette.card, p: 3, height: '100%',
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: palette.text, mb: 2 }}>
                  What you get on Premium
                </Typography>
                {[
                  ['ph:speedometer', '60 requests a minute', 'Measured over a rolling minute, per key'],
                  ['ph:chart-line-up', '100,000 requests a month', 'Resets on the first of the month'],
                  ['ph:key', '5 keys at a time', 'Create and revoke them yourself'],
                  ['ph:shield-check', 'Scoped keys', 'Give each integration only what it needs'],
                ].map(([icon, title, sub]) => (
                  <Box key={title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
                    <Icon icon={icon} width={18} height={18} color={palette.accent} style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: palette.text }}>
                        {title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, lineHeight: 1.5 }}>
                        {sub}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* =========================== 02 Authentication ========================== */}
      <Box component="section" sx={sectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            number="02"
            eyebrow="Authentication"
            title="One header, one key"
            subtitle="Create a key in your workspace, then send it on every request."
          />

          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={7}>
              <Typography sx={{ fontSize: '0.95rem', color: palette.textSecondary, lineHeight: 1.75, mb: 2.5 }}>
                Sign in, open{' '}
                <Box component={RouterLink} to="/app/api-keys" sx={{ color: palette.accent, fontWeight: 600, textDecoration: 'none' }}>
                  API keys
                </Box>{' '}
                and create one. The value is shown once, at that moment, and never again:
                we store a hash of it rather than the key itself, so nobody at Farmgate can
                read it back to you. If you lose it, revoke the key and make another.
              </Typography>

              <Code palette={palette} label="A request" onCopy={copy}>
{`curl ${BASE_URL}/prices?limit=5 \\
  -H "X-API-Key: fg_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"`}
              </Code>

              <Typography sx={{ fontSize: '0.85rem', color: palette.textSecondary, lineHeight: 1.65, mb: 2.5 }}>
                Send the key in <Box component="span" sx={{ fontFamily: MONO, color: palette.text }}>X-API-Key</Box>.
                An <Box component="span" sx={{ fontFamily: MONO, color: palette.text }}>Authorization: Bearer</Box>{' '}
                header will not work here: that one carries browser sessions, and keeping
                the two apart means a leaked key can never be mistaken for a signed-in
                person.
              </Typography>

              <Code palette={palette} label="Without a key">
{`{"detail":"Provide a valid API key in the X-API-Key header."}`}
              </Code>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ border: `1px solid ${palette.cardBorder}`, borderRadius: '16px', bgcolor: palette.card, p: 3 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: palette.text, mb: 0.75 }}>
                  Scopes
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: palette.textSecondary, lineHeight: 1.6, mb: 2 }}>
                  You choose these when you create a key, and can change them afterwards
                  without the key value changing. A key without a scope gets a 403 from the
                  endpoints it covers, so narrow each key to what its integration actually
                  reads.
                </Typography>
                {SCOPES.map(([scope, desc, path]) => (
                  <Box key={scope} sx={{ mb: 1.75 }}>
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', fontWeight: 700, color: palette.text }}>
                      {scope}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary }}>
                      {desc} <Box component="span" sx={{ fontFamily: MONO, color: palette.textMuted }}>({path})</Box>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ============================= 03 Endpoints ============================ */}
      <Box component="section" sx={sectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            number="03"
            eyebrow="Reference"
            title="The endpoints"
            subtitle="Five of them. Every example below is a real response from the live API."
          />

          <Box sx={{ mt: 1 }}>
            <Endpoint
              palette={palette}
              onCopy={copy}
              path="/prices"
              summary="Price observations from market surveys. The largest dataset here, and the only one that paginates by default."
              params={[
                ['commodity', 'A commodity id, such as COM001. Omit for all.'],
                ['market', 'A market id, such as MKT001. Omit for all.'],
                ['from', 'Earliest observation date, YYYY-MM-DD.'],
                ['to', 'Latest observation date, YYYY-MM-DD.'],
                ['limit', 'Rows to return. Defaults to 100, capped at 1000. A larger value is clamped rather than refused.'],
                ['offset', 'Rows to skip, for paging through the total.'],
              ]}
              response={`{
  "data": [
    {
      "observedOn": "2026-06-15",
      "commodityId": "COM001",
      "marketId": "MKT001",
      "price": 70000.0,
      "pricePerKg": 1000.0,
      "bagSizeKg": 70,
      "season": "NEW",
      "currency": "NGN"
    }
  ],
  "pagination": { "total": 1177, "limit": 1, "offset": 0 },
  "note": "Prices come from market surveys uploaded weekly. Each row carries the date it was observed."
}`}
              note={
                <>
                  <Box component="span" sx={{ fontFamily: MONO }}>price</Box> is for one bag of{' '}
                  <Box component="span" sx={{ fontFamily: MONO }}>bagSizeKg</Box> kilograms;{' '}
                  <Box component="span" sx={{ fontFamily: MONO }}>pricePerKg</Box> is that divided
                  through, so you can compare across bag sizes without doing it yourself.{' '}
                  <Box component="span" sx={{ fontFamily: MONO }}>season</Box> distinguishes new-harvest
                  stock from old.
                </>
              }
            />

            <Endpoint
              palette={palette}
              onCopy={copy}
              path="/commodities"
              summary="The commodity catalogue, so you can turn a commodityId into something a person can read. Retired commodities are left out."
              response={`{
  "data": [
    {
      "id": "COM001",
      "name": "Paddy Rice Long Grain",
      "canonicalName": "Paddy Rice Long",
      "categoryId": "CAT001",
      "bagSizesKg": [75, 100]
    }
  ]
}`}
              note="Returns the whole catalogue, around fifty entries, with no paging. It is small and you will want all of it. Cache it and refresh occasionally rather than fetching it alongside every price call."
            />

            <Endpoint
              palette={palette}
              onCopy={copy}
              path="/markets"
              summary="The market directory, with the state and local government each market sits in."
              response={`{
  "data": [
    {
      "id": "MKT001",
      "name": "Giwa",
      "stateCode": "KD",
      "stateName": "Kaduna State",
      "lga": "Giwa"
    }
  ]
}`}
              note="Like the commodity catalogue, this returns everything and does not page."
            />

            <Endpoint
              palette={palette}
              onCopy={copy}
              path="/fx"
              summary="Naira exchange rates, newest first."
              params={[
                ['pair', 'NGN/USD for the market rate, or NGN/USD-OFFICIAL for CBN\'s published rate. Defaults to NGN/USD.'],
                ['from', 'Earliest date, YYYY-MM-DD.'],
                ['to', 'Latest date, YYYY-MM-DD.'],
                ['limit', 'Rows to return. Defaults to 100, capped at 1000.'],
                ['offset', 'Rows to skip.'],
              ]}
              response={`{
  "pair": "NGN/USD",
  "data": [
    {
      "observedOn": "2026-08-24",
      "rate": 1349.99,
      "open": null,
      "high": 1350.0,
      "low": 1345.0,
      "close": 1349.99,
      "source": "external:cbn-nfem"
    }
  ],
  "pagination": { "total": 54, "limit": 1, "offset": 0 }
}`}
              note={
                <>
                  <Box component="span" sx={{ fontFamily: MONO }}>open</Box>,{' '}
                  <Box component="span" sx={{ fontFamily: MONO }}>high</Box> and{' '}
                  <Box component="span" sx={{ fontFamily: MONO }}>low</Box> can be null: not every
                  source publishes an intraday range, and we would rather return null than invent
                  one. <Box component="span" sx={{ fontFamily: MONO }}>rate</Box> is always present.
                  The newest row is the newest rate the source has published, which may be a day or
                  two behind today.
                </>
              }
            />

            <Endpoint
              palette={palette}
              onCopy={copy}
              path="/usage"
              summary="What this key has spent this month and what is left. Useful for alerting yourself before a quota runs out rather than after."
              response={`{
  "requestsThisMonth": 5,
  "requestsPerMonth": 100000,
  "remaining": 99995,
  "ratePerMinute": 60,
  "resetsOn": "2026-09-01"
}`}
              note="Requires the read:prices scope. This call counts against your quota like any other, so poll it occasionally rather than before every request."
            />
          </Box>
        </Container>
      </Box>

      {/* =========================== 04 Limits + errors ========================= */}
      <Box component="section" sx={{ ...sectionSx, pb: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            number="04"
            eyebrow="Limits and errors"
            title="What happens when you go too fast"
            subtitle="Both limits answer with 429 and a Retry-After header."
          />

          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={7}>
              <Typography sx={{ fontSize: '0.95rem', color: palette.textSecondary, lineHeight: 1.75, mb: 2.5 }}>
                Each key may make 60 requests a minute and 100,000 a month. The per-minute
                limit is measured over a rolling window rather than a clock minute, so
                bursting across the top of a minute does not hand you a second allowance.
                Pace long jobs at roughly one request a second and you will never see a 429.
              </Typography>

              <Code palette={palette} label="Too many requests">
{`HTTP/1.1 429 Too Many Requests
Retry-After: 60

{"detail":"Rate limit of 60 requests a minute reached. Try again shortly."}`}
              </Code>

              <Typography sx={{ fontSize: '0.85rem', color: palette.textSecondary, lineHeight: 1.65 }}>
                Wait for the number of seconds in{' '}
                <Box component="span" sx={{ fontFamily: MONO, color: palette.text }}>Retry-After</Box>{' '}
                and try again. Retrying immediately just spends another refusal.
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${palette.cardBorder}`, borderRadius: '16px', bgcolor: palette.card }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Status', 'What it means'].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: palette.textMuted,
                            borderBottom: `1px solid ${palette.divider}`,
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ERRORS.map(([code, meaning]) => (
                      <TableRow key={code}>
                        <TableCell sx={{ fontFamily: MONO, fontSize: '0.8rem', fontWeight: 700, color: palette.text, borderBottom: `1px solid ${palette.divider}`, verticalAlign: 'top' }}>
                          {code}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: palette.textSecondary, lineHeight: 1.55, borderBottom: `1px solid ${palette.divider}` }}>
                          {meaning}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography sx={{ fontSize: '0.79rem', color: palette.textMuted, lineHeight: 1.6, mt: 2 }}>
                Every error body is a JSON object with a single{' '}
                <Box component="span" sx={{ fontFamily: MONO }}>detail</Box> field, written to be
                shown to a person.
              </Typography>
            </Grid>
          </Grid>

          {/* Closing call to action */}
          <Box
            sx={{
              mt: { xs: 5, md: 7 }, p: { xs: 3, md: 4 },
              borderRadius: '24px', bgcolor: palette.navy, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 3, flexWrap: 'wrap',
            }}
          >
            <Box sx={{ maxWidth: 560 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', mb: 0.75 }}>
                Ready to build?
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.65 }}>
                API access comes with Premium. Create your first key in your workspace, and
                you can be reading prices in a couple of minutes.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/app/api-keys"
                variant="contained"
                disableElevation
                sx={{
                  bgcolor: palette.accent, color: '#fff', fontWeight: 600,
                  textTransform: 'none', borderRadius: '9px', px: 3,
                  '&:hover': { bgcolor: '#1C9550' },
                }}
              >
                Get a key
              </Button>
              <Button
                component={RouterLink}
                to="/app/subscription"
                variant="outlined"
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: '9px', px: 3,
                  borderColor: 'rgba(255,255,255,0.28)', color: '#fff',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                See plans
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast && <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</MuiAlert>}
      </Snackbar>
    </Box>
  );
}

export default Developers;
